import React, { useCallback, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Chart } from './components/Chart/Chart';
import { Table } from './components/Table/Table';
import { LoadingLongevityAnalysis } from './LongevityAnalysis.loading';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../hooks/useFpId';
import { queryKeys } from '../../../../api/queryKeys';
import { useLongevityAnalysisQuery } from '../../../../api/useLongevityAnalysisQuery';
import { useLongevityAnalysisWhatIfsMutation } from '../../../../api/useLongevityAnalysisWhatIfsMutation';
import { ErrorState } from '../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import {
  whatIfsBodySelector,
  whatIfsOrDefaultSelector,
} from '../../../../../../WhatIfs/WhatIfsSelector';

export const LogevityAnalysis = () => {
  const queryClient = useQueryClient();
  const clientId = useClientId();
  const fpId = useFpId();

  const whatIfsBody = useSelector(whatIfsBodySelector);
  const isWhatIfsOpen = useSelector(whatIfsOrDefaultSelector).open;
  const isWhatIfsActive = isWhatIfsOpen && whatIfsBody != null;

  const {
    isFetching: isQueryFetching,
    data: queryData,
    error: queryError,
    refetch,
  } = useLongevityAnalysisQuery({
    clientId,
    fpId,
    enabled: !isWhatIfsActive,
  });

  const mutation = useLongevityAnalysisWhatIfsMutation({ clientId, fpId });

  useEffect(() => {
    if (isWhatIfsActive) {
      mutation.mutate({ clientId, fpId, body: whatIfsBody });
    }
  }, [isWhatIfsActive, whatIfsBody, clientId, fpId]);

  // The what-ifs mutation fires in a post-mount effect, so on the first render it is
  // still idle (not loading, no data). Treat that idle window as loading to avoid a
  // blank flash before the mutate call runs.
  const isLoading = isWhatIfsActive ? mutation.isLoading || mutation.isIdle : isQueryFetching;
  const data = isWhatIfsActive ? mutation.data : queryData;
  const error = isWhatIfsActive ? mutation.error : queryError;

  const handleRetry = useCallback(() => {
    if (isWhatIfsActive && whatIfsBody != null) {
      mutation.mutate({ clientId, fpId, body: whatIfsBody });
    } else {
      refetch();
    }
  }, [isWhatIfsActive, whatIfsBody, mutation, clientId, fpId, refetch]);

  // When what-ifs are active the visible data comes from the mutation while the
  // baseline query is disabled, so invalidating the query alone won't refresh it.
  // Re-run the what-ifs mutation to reflect the newly persisted gender.
  const handleGenderSaved = useCallback(() => {
    if (isWhatIfsActive && whatIfsBody != null) {
      mutation.mutate({ clientId, fpId, body: whatIfsBody });
    } else {
      queryClient.invalidateQueries(queryKeys.longevity(clientId, fpId));
    }
  }, [isWhatIfsActive, whatIfsBody, mutation, clientId, fpId, queryClient]);

  if (isLoading) {
    return <LoadingLongevityAnalysis />;
  }

  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py="24" height="100%">
        <ErrorState onAction={handleRetry} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Box position="relative">
      <Box display="flex" flexDirection="column" css={({ theme }) => ({ gap: theme.space['16'] })}>
        <Text variant="cardTitle" pt="32" pb="4">
          Probability of Annual Success
        </Text>

        <Chart data={data} />

        <Table data={data} onGenderSaved={handleGenderSaved} />
      </Box>
    </Box>
  );
};
